/**
 * Reusable file download utilities.
 * Provides functions for downloading files in the browser using Blob and anchor elements.
 */

/**
 * Downloads a Blob as a file in the browser.
 *
 * @param blob - The Blob to download
 * @param fileName - The name for the downloaded file
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = fileName
  anchor.style.display = 'none'

  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)

  URL.revokeObjectURL(url)
}

/**
 * Downloads content as a CSV file.
 *
 * @param content - The CSV content as a string
 * @param fileName - The name for the downloaded file (should end with .csv)
 */
export function downloadCsv(content: string, fileName: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, fileName)
}

/**
 * Downloads content as a JSON file.
 *
 * @param content - The data to serialize as JSON
 * @param fileName - The name for the downloaded file (should end with .json)
 */
export function downloadJson(content: unknown, fileName: string): void {
  const jsonString = JSON.stringify(content, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8;' })
  downloadBlob(blob, fileName)
}

/**
 * Downloads plain text content as a file.
 *
 * @param content - The text content
 * @param fileName - The name for the downloaded file
 * @param mimeType - Optional MIME type (default: text/plain)
 */
export function downloadText(content: string, fileName: string, mimeType = 'text/plain'): void {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` })
  downloadBlob(blob, fileName)
}

/**
 * Generates a timestamped filename.
 *
 * @param prefix - The filename prefix
 * @param extension - The file extension (without dot)
 * @returns A filename with ISO date timestamp
 */
export function generateTimestampedFileName(prefix: string, extension: string): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
  return `${prefix}-${timestamp}.${extension}`
}
